import { useId, useState } from 'react';
import type { Formatter } from './types';

interface HeatmapProps {
  title: string;
  summary?: string | undefined;
  /** Row labels, top to bottom — days of the week, benches. */
  rows: readonly string[];
  /** Column labels, left to right — hours of the day. */
  columns: readonly string[];
  /**
   * `rows.length` × `columns.length`.
   *
   * `null` means **no data**, not zero — a Sunday the lab was shut is not a
   * Sunday with no visits, and the two are drawn differently.
   */
  cells: readonly (readonly (number | null)[])[];
  format?: Formatter | undefined;
  /** Every nth column label, for a 24-hour axis on a narrow screen. */
  columnStride?: number | undefined;
}

/**
 * Intensity across two categorical axes.
 *
 * Built for "when is the lab busy" — hour of day against day of week — which is
 * the one question a line chart genuinely cannot answer, because it has two
 * independent categorical dimensions and one measure.
 *
 * Rendered as a CSS grid of `<div>`s rather than SVG. Cells need to be
 * individually focusable and to carry a tooltip, and a grid of divs gets
 * keyboard navigation, hit testing and text wrapping from the platform. SVG
 * would mean reimplementing all three.
 *
 * Colour is a single-hue sequential ramp — the brand indigo at varying alpha —
 * not a rainbow. A rainbow scale implies category boundaries that intensity
 * does not have, and reads in a different order for different people.
 */
export function Heatmap({
  title,
  summary,
  rows,
  columns,
  cells,
  format = String,
  columnStride = 1,
}: HeatmapProps) {
  const tableId = useId();
  const [active, setActive] = useState<{ row: number; column: number } | undefined>();

  const readings = cells.flat().filter((value): value is number => value !== null);
  const max = readings.length > 0 ? Math.max(...readings) : 0;

  const activeLabel =
    active && cells[active.row]?.[active.column] != null
      ? `${rows[active.row]} ${columns[active.column]}: ${format(cells[active.row]![active.column]!)}`
      : '';

  return (
    <figure className="c-chart c-heatmap">
      <div
        className="c-heatmap__grid"
        style={{ '--c-heatmap-columns': columns.length } as React.CSSProperties}
        role="img"
        aria-label={`${title}. ${summary ?? ''}`.trim()}
        aria-describedby={tableId}
      >
        {/* Corner spacer, then the column header row. */}
        <span className="c-heatmap__corner" aria-hidden="true" />
        {columns.map((column, index) => (
          <span key={column} className="c-heatmap__column-label" aria-hidden="true">
            {index % columnStride === 0 ? column : ''}
          </span>
        ))}

        {rows.map((row, rowIndex) => (
          <Row
            key={row}
            row={row}
            rowIndex={rowIndex}
            columns={columns}
            values={cells[rowIndex] ?? []}
            max={max}
            format={format}
            onHover={setActive}
          />
        ))}
      </div>

      {summary && <figcaption className="c-chart__summary">{summary}</figcaption>}

      <p className="c-heatmap__readout tnum" aria-hidden="true">
        {activeLabel || ' '}
      </p>

      {/* The numbers, navigable. Same reasoning as ChartTable: a 168-cell
          aria-label is not something anyone can listen to. */}
      <table id={tableId} className="sr-only">
        <caption>{title} — data table</caption>
        <thead>
          <tr>
            <th scope="col">Row</th>
            {columns.map((column) => (
              <th key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={row}>
              <th scope="row">{row}</th>
              {columns.map((column, columnIndex) => {
                const value = cells[rowIndex]?.[columnIndex];
                return <td key={column}>{value == null ? 'No data' : format(value)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

function Row({
  row,
  rowIndex,
  columns,
  values,
  max,
  format,
  onHover,
}: {
  row: string;
  rowIndex: number;
  columns: readonly string[];
  values: readonly (number | null)[];
  max: number;
  format: Formatter;
  onHover: (cell: { row: number; column: number } | undefined) => void;
}) {
  return (
    <>
      <span className="c-heatmap__row-label" aria-hidden="true">
        {row}
      </span>
      {columns.map((column, columnIndex) => {
        const value = values[columnIndex] ?? null;
        // Zero gets a floor of intensity so it still reads as a cell that was
        // measured. No data gets none, and a hatch, so the two never look alike.
        const intensity = value === null ? 0 : max === 0 ? 0.08 : 0.08 + (value / max) * 0.92;

        return (
          <span
            key={column}
            className={`c-heatmap__cell${value === null ? ' is-empty' : ''}`}
            style={{ '--c-heatmap-intensity': intensity } as React.CSSProperties}
            title={`${row} ${column}: ${value === null ? 'No data' : format(value)}`}
            onPointerEnter={() => onHover({ row: rowIndex, column: columnIndex })}
            onPointerLeave={() => onHover(undefined)}
            aria-hidden="true"
          />
        );
      })}
    </>
  );
}
